import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Axios from "axios";
import Constant from "../Constant";
import { getAccessToken } from "../../hooks/AccessToken";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import "../../css/style.css";

const SimpleEventsManagement = () => {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    setError("");
    try {
      const token = getAccessToken();
      const response = await Axios.get(
        Constant.apiBasePath + "simple-events/admin/get-all",
        { headers: { token } }
      );
      if (response.data.status === Constant.statusSuccess) {
        setEvents(response.data.data || []);
      } else {
        setError(response.data.message || "Failed to fetch events");
      }
    } catch (err) {
      setError("Error fetching events");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    setLoading(true);
    setError("");
    try {
      const token = getAccessToken();
      const response = await Axios.delete(
        Constant.apiBasePath + `simple-events/admin/delete/${id}`,
        { headers: { token } }
      );
      if (response.data.status === Constant.statusSuccess) {
        setEvents(events.filter((event) => event._id !== id));
      } else {
        setError(response.data.message || "Failed to delete event");
      }
    } catch (err) {
      setError("Error deleting event");
    }
    setLoading(false);
  };

  const filteredEvents =
    filter === "all"
      ? events
      : events.filter((event) =>
          filter === "active" ? event.status === 1 : event.status === 0
        );

  const totalPages = Math.max(
    1,
    Math.ceil((filteredEvents.length || 0) / itemsPerPage)
  );
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const currentItems = filteredEvents.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="content-page contest-submissions-page contest-management-page simple-events-consistent-page">
      <div className="content page-content-spaced">
        <div className="event-management-header">
          <div className="header-content">
            <div className="header-title">
              <h4 className="page-title-heading">Simple Events Management</h4>
              <p className="header-subtitle">
                Manage simple events, status, and sub event flows.
              </p>
            </div>
            <div className="header-actions">
              <select
                className="form-control filter-select"
                style={{ width: 160 }}
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <Link to="/simple-events/add" className="btn btn-primary btn-add-event contest-add-match">
                + Add New
              </Link>
            </div>
          </div>
        </div>

        <div className="event-management-body">
          <div className="card contest-submissions-card contest-like-card">
            <div className="card-body">
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="table-wrapper contest-submissions-table-responsive contest-like-table-responsive">
                <table className="table event-table contest-submissions-table contest-like-table">
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th className="actions-col">Actions</th>
                      <th>Sub Events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="table-loading-cell">
                          Loading simple events...
                        </td>
                      </tr>
                    ) : currentItems.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="table-loading-cell">
                          No events found.
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((event, index) => (
                        <tr key={event._id} className="table-row">
                          <td>{startIndex + index + 1}</td>
                          <td className="namecol">{event.name}</td>
                          <td className="description">
                            <div className="description-text">{event.description}</div>
                          </td>
                          <td className="contest-status-col">
                            <span
                              className={`contest-status-pill ${
                                event.status === 1 ? "is-active" : "is-inactive"
                              }`}
                            >
                              {event.status === 1 ? "ACTIVE" : "INACTIVE"}
                            </span>
                          </td>
                          <td className="actions-col">
                            <Link
                              to={`/simple-events/edit/${event._id}`}
                              className="contest-icon-btn open-sessions-icon-btn"
                              aria-label="Edit simple event"
                            >
                              <FontAwesomeIcon icon={faEdit} style={{ fontSize: "13px" }} />
                            </Link>
                            <button
                              className="contest-icon-btn open-sessions-icon-btn danger"
                              onClick={() => handleDelete(event._id)}
                              type="button"
                              aria-label="Delete simple event"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: "13px" }} />
                            </button>
                          </td>
                          <td>
                            <Link
                              to={`/simple-events/${event._id}/sub-events`}
                              className="btn btn-info"
                            >
                              Sub Events
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="pagination">
                <button
                  type="button"
                  className={safePage === 1 ? "disabled" : "paginate-link"}
                  disabled={safePage === 1}
                  onClick={() => setCurrentPage(safePage - 1)}
                >
                  Previous
                </button>
                <button type="button" className="page-number">
                  {safePage}/{totalPages}
                </button>
                <button
                  type="button"
                  className={safePage === totalPages ? "disabled" : "paginate-link active"}
                  disabled={safePage === totalPages}
                  onClick={() => setCurrentPage(safePage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleEventsManagement;
